import React from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const Terms = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            <span className="font-bold">Aurex AI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-black mb-2">Terms and Conditions</h1>
        <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

        <div className="prose prose-invert prose-sm max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Welcome to Aurex AI ("Company", "we", "our", "us"). These Terms and Conditions ("Terms", "Agreement") govern your use of our website located at aurexai.cloud and our trading signal generation services (together or individually, the "Service") operated by Aurex AI.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of the terms, you do not have permission to access the Service.
            </p>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">2. Service Description</h2>
            <p className="text-muted-foreground leading-relaxed">
              Aurex AI provides algorithmic trading signal generation services for financial instruments including but not limited to XAUUSD (Gold), XAGUSD (Silver), EURUSD, AUDUSD, and USDJPY. Our Service includes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Rule-based market analysis and trade signal generation</li>
              <li>Entry, Stop Loss, and Take Profit level recommendations</li>
              <li>Market bias analysis across multiple timeframes</li>
              <li>Economic calendar integration and news event alerts</li>
              <li>Confidence level assessments for generated signals</li>
            </ul>
          </section>

          {/* Important Disclaimer */}
          <section className="bg-red-500/10 border border-red-500/30 p-6 rounded-lg">
            <h2 className="text-xl font-bold text-red-400 mb-4">3. IMPORTANT DISCLAIMER - NOT FINANCIAL ADVICE</h2>
            <p className="text-red-200 leading-relaxed font-medium">
              THE SERVICE PROVIDED BY AUREX AI IS FOR INFORMATIONAL AND EDUCATIONAL PURPOSES ONLY. THE TRADING SIGNALS, ANALYSIS, AND ANY OTHER INFORMATION PROVIDED DO NOT CONSTITUTE FINANCIAL ADVICE, INVESTMENT ADVICE, TRADING ADVICE, OR ANY OTHER SORT OF ADVICE.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              You understand and acknowledge that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li><strong className="text-white">Trading involves substantial risk of loss</strong> and is not suitable for all investors. You should carefully consider whether trading is appropriate for you in light of your financial condition.</li>
              <li><strong className="text-white">Past performance is not indicative of future results.</strong> Historical data and backtesting results do not guarantee future profits.</li>
              <li><strong className="text-white">You are solely responsible for your trading decisions.</strong> Any trades you make are at your own risk and discretion.</li>
              <li><strong className="text-white">We do not guarantee profits or prevent losses.</strong> Trading signals are generated algorithmically and may result in losses.</li>
              <li><strong className="text-white">You should seek independent financial advice</strong> from a qualified professional before making any investment decisions.</li>
            </ul>
          </section>

          {/* User Eligibility */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">4. User Eligibility</h2>
            <p className="text-muted-foreground leading-relaxed">
              By using our Service, you represent and warrant that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>You are at least 18 years of age or the legal age of majority in your jurisdiction</li>
              <li>You have the legal capacity to enter into binding contracts</li>
              <li>You are not prohibited from using the Service under any applicable laws</li>
              <li>You understand the risks associated with trading financial instruments</li>
              <li>Trading is legal in your jurisdiction and you comply with all local laws and regulations</li>
            </ul>
          </section>

          {/* Account Registration */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">5. Account Registration and Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              To access certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Notify us immediately of any unauthorized access or security breach</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
            </p>
          </section>

          {/* Subscription and Payments */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">6. Subscription and Payments</h2>
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">6.1 Free Trial</h3>
            <p className="text-muted-foreground leading-relaxed">
              New users receive 4 free signal generations to evaluate our Service. Free trials are limited to one per user/email address. Abuse of the free trial system may result in account termination.
            </p>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">6.2 Paid Subscriptions</h3>
            <p className="text-muted-foreground leading-relaxed">
              We offer the following subscription plans:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li><strong className="text-white">Monthly Plan:</strong> $30 USD per month</li>
              <li><strong className="text-white">Annual Plan:</strong> $306 USD per year (15% discount)</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">6.3 Payment Methods</h3>
            <p className="text-muted-foreground leading-relaxed">
              We accept payments via:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>USDT (Tether) on BEP-20 network (Binance Smart Chain)</li>
              <li>USDT (Tether) on TRC-20 network (Tron)</li>
              <li>Other payment methods as made available</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Payments are processed manually and may take up to 4 hours for verification, though most are confirmed within 15 minutes.
            </p>
          </section>

          {/* Refund Policy */}
          <section className="bg-amber-500/10 border border-amber-500/30 p-6 rounded-lg">
            <h2 className="text-xl font-bold text-amber-400 mb-4">7. REFUND POLICY - NO REFUNDS</h2>
            <p className="text-amber-200 leading-relaxed font-medium">
              ALL SALES ARE FINAL. WE DO NOT OFFER REFUNDS FOR ANY SUBSCRIPTION PAYMENTS UNDER ANY CIRCUMSTANCES.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              By purchasing a subscription, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>You have evaluated our Service using the free trial before purchasing</li>
              <li>Digital services are immediately accessible upon payment confirmation</li>
              <li>No refunds will be issued for partial months, unused signals, or dissatisfaction with trading results</li>
              <li>Chargebacks or payment disputes may result in immediate account termination</li>
              <li>We reserve the right to pursue legal action for fraudulent chargebacks</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong className="text-white">Exception:</strong> If you have not received access to paid features due to a technical error on our part within 48 hours of payment confirmation, please contact support for resolution.
            </p>
          </section>

          {/* Subscription Cancellation */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">8. Subscription Cancellation</h2>
            <p className="text-muted-foreground leading-relaxed">
              You may cancel your subscription at any time. Upon cancellation:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Your subscription will remain active until the end of the current billing period</li>
              <li>No refunds will be provided for the remaining subscription period</li>
              <li>You will retain access to all paid features until the subscription expires</li>
              <li>Your account will revert to free status after expiration</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">9. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, AUREX AI AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Any trading losses, whether direct or indirect, incurred from using our signals</li>
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Service interruptions, delays, or technical failures</li>
              <li>Errors or inaccuracies in the trading signals or market analysis</li>
              <li>Actions of third parties, including brokers or trading platforms</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Our total liability for any claims arising from or related to the Service shall not exceed the amount you paid for the Service in the three (3) months preceding the claim.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">10. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service, including all content, features, algorithms, software, designs, and trademarks, are owned by Aurex AI and protected by intellectual property laws. You may not:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Copy, modify, or distribute our content without permission</li>
              <li>Reverse engineer or attempt to extract our algorithms</li>
              <li>Resell, redistribute, or commercially exploit our signals</li>
              <li>Use automated systems to access or scrape our Service</li>
              <li>Remove any copyright or proprietary notices</li>
            </ul>
          </section>

          {/* Prohibited Activities */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">11. Prohibited Activities</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Use the Service for any illegal purpose or in violation of any laws</li>
              <li>Share your account credentials with others</li>
              <li>Create multiple accounts to abuse free trials</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Impersonate any person or entity</li>
              <li>Engage in any activity that could damage our reputation</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">12. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to suspend or terminate your account and access to the Service at our sole discretion, without notice, for conduct that we believe:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Violates these Terms or any applicable laws</li>
              <li>Is harmful to other users, us, or third parties</li>
              <li>Involves fraudulent or illegal activity</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Upon termination, your right to use the Service will immediately cease. No refunds will be provided for terminations due to Terms violations.
            </p>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">13. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to indemnify, defend, and hold harmless Aurex AI and its affiliates, officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, and expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Your trading activities and decisions</li>
            </ul>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">14. Governing Law and Disputes</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms shall be governed by and construed in accordance with international commercial law principles. Any disputes arising from these Terms or the Service shall be resolved through:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Good faith negotiation between the parties</li>
              <li>Mediation if negotiation fails</li>
              <li>Binding arbitration as a last resort</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              You agree to waive any right to participate in class action lawsuits against Aurex AI.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">15. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of the Service after any changes constitutes acceptance of the new Terms. We encourage you to review these Terms periodically.
            </p>
          </section>

          {/* Severability */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">16. Severability</h2>
            <p className="text-muted-foreground leading-relaxed">
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">17. Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-secondary/30 border border-border">
              <p className="text-white font-medium">Aurex AI</p>
              <p className="text-muted-foreground">Email: admin@aurexai.cloud</p>
              <p className="text-muted-foreground">Website: https://aurexai.cloud</p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="border-t border-border pt-8">
            <p className="text-muted-foreground leading-relaxed">
              By using Aurex AI, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </section>
        </div>

        {/* Back to top */}
        <div className="mt-12 pt-8 border-t border-border flex justify-between items-center">
          <Link to="/privacy" className="text-sm text-muted-foreground hover:text-white transition-colors">
            Privacy Policy →
          </Link>
          <Link to="/">
            <Button variant="outline" size="sm">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-muted-foreground">
            © 2025 Aurex AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
